'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Brain, Target, Wifi, WifiOff, Loader2, Zap } from 'lucide-react';
import { eegMetricsService } from '@/lib/services/eegMetricsService';

interface EEGApiResponse {
  timestamp: string;
  cognitive_load: number;
  attention_score: number;
  signal_quality: number;
}

type FetchStatus = 'idle' | 'loading' | 'success' | 'error';

// Generate a 2-second window of mock EEG data (256 Hz × 8 channels × 2s = 512 samples/channel)
function generateEEGBatch() {
  const sampleRate = 256;
  const windowSeconds = 2;
  const numSamples = sampleRate * windowSeconds;
  const channels = ['Fp1', 'Fp2', 'F3', 'F4', 'C3', 'C4', 'P3', 'P4'];

  const data: Record<string, number[]> = {};
  channels.forEach((ch) => {
    data[ch] = Array.from({ length: numSamples }, (_, i) => {
      const t = i / sampleRate;
      return (
        Math.sin(2 * Math.PI * 10 * t) * 15 +
        Math.sin(2 * Math.PI * 20 * t) * 8 +
        (Math.random() - 0.5) * 10
      );
    });
  });

  return {
    sample_rate: sampleRate,
    window_seconds: windowSeconds,
    channels: data,
    timestamp: new Date().toISOString(),
  };
}

export default function LiveEEGIndicator() {
  const [cognitiveLoad, setCognitiveLoad] = useState<number | null>(null);
  const [attentionScore, setAttentionScore] = useState<number | null>(null);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('idle');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const apiUrl = process.env.NEXT_PUBLIC_EEG_API_URL || '';

  const sendBatch = async () => {
    if (!apiUrl) {
      setFetchStatus('error');
      setErrorMsg('NEXT_PUBLIC_EEG_API_URL not set');
      return;
    }

    setFetchStatus('loading');
    try {
      const payload = generateEEGBatch();
      const res = await fetch(`${apiUrl}/process-eeg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const json: EEGApiResponse = await res.json();
      setCognitiveLoad(json.cognitive_load);
      setAttentionScore(json.attention_score);
      setFetchStatus('success');
      setErrorMsg(null);
      setLastUpdated(new Date().toLocaleTimeString());

      // Persist to Supabase using the API-provided timestamp
      await eegMetricsService.insert({
        cognitiveLoad: json.cognitive_load,
        attentionScore: json.attention_score,
        signalQuality: json.signal_quality ?? null,
        recordedAt: json.timestamp,
      });
    } catch (err: unknown) {
      setFetchStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Request failed');
    }
  };

  useEffect(() => {
    sendBatch();
    intervalRef.current = setInterval(sendBatch, 2000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clPercent = cognitiveLoad !== null ? Math.round(cognitiveLoad * 100) : null;
  const atPercent = attentionScore !== null ? Math.round(attentionScore * 100) : null;

  const clColor =
    clPercent === null ? 'text-zinc-400' :
    clPercent >= 75 ? 'text-red-400' :
    clPercent >= 50 ? 'text-amber-400' : 'text-emerald-400';

  const atColor =
    atPercent === null ? 'text-zinc-400' :
    atPercent >= 70 ? 'text-sky-400' :
    atPercent >= 45 ? 'text-amber-400' : 'text-red-400';

  const clBarColor =
    clPercent === null ? '' :
    clPercent >= 75 ? 'from-red-600 to-red-400' :
    clPercent >= 50 ? 'from-amber-600 to-[#F97316]' : 'from-emerald-600 to-emerald-400';

  const atBarColor =
    atPercent === null ? '' :
    atPercent >= 70 ? 'from-sky-600 to-sky-400' :
    atPercent >= 45 ? 'from-amber-600 to-[#F97316]' : 'from-red-600 to-red-400';

  return (
    <div className="rounded-xl border border-[#1E3A5F]/60 bg-[#0B1628]/80 p-4 backdrop-blur"
      style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(249,115,22,0.06)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={13} className="text-[#F97316]" />
          <span className="text-[11px] font-bold text-[#F97316] uppercase tracking-widest">
            Live API — /process-eeg
          </span>
          {fetchStatus === 'loading' && (
            <Loader2 size={12} className="text-[#F97316]/60 animate-spin" />
          )}
          {fetchStatus === 'success' && (
            <Wifi size={12} className="text-emerald-400" />
          )}
          {fetchStatus === 'error' && (
            <WifiOff size={12} className="text-red-400" />
          )}
        </div>
        {lastUpdated && (
          <span className="text-[10px] text-[#2D5A8E] font-mono">Updated {lastUpdated}</span>
        )}
      </div>

      {/* Indicators */}
      <div className="grid grid-cols-2 gap-3">
        {/* Cognitive Load */}
        <div className="rounded-xl border border-[#1E3A5F]/50 bg-[#060D1A]/60 p-4 flex flex-col gap-3 relative overflow-hidden group hover:border-[#F97316]/30 transition-all duration-300">
          {/* Background glow on hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: 'radial-gradient(circle at 50% 100%, rgba(249,115,22,0.05) 0%, transparent 70%)' }}
          />
          <div className="flex items-center gap-1.5 relative z-10">
            <Brain size={13} className="text-violet-400" />
            <span className="text-[11px] text-zinc-400 font-semibold">Cognitive Load</span>
          </div>
          <div className="flex items-end gap-1 relative z-10">
            <span className={`text-4xl font-bold font-mono tabular-nums ${clColor}`}>
              {clPercent !== null ? clPercent : '—'}
            </span>
            <span className="text-[12px] text-zinc-500 mb-1">%</span>
          </div>
          <div className="h-2 rounded-full bg-[#1E3A5F]/40 overflow-hidden relative z-10">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${clBarColor} transition-all duration-700`}
              style={{ width: clPercent !== null ? `${clPercent}%` : '0%' }}
            />
          </div>
        </div>

        {/* Attention Score */}
        <div className="rounded-xl border border-[#1E3A5F]/50 bg-[#060D1A]/60 p-4 flex flex-col gap-3 relative overflow-hidden group hover:border-[#F97316]/30 transition-all duration-300">
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: 'radial-gradient(circle at 50% 100%, rgba(56,189,248,0.05) 0%, transparent 70%)' }}
          />
          <div className="flex items-center gap-1.5 relative z-10">
            <Target size={13} className="text-sky-400" />
            <span className="text-[11px] text-zinc-400 font-semibold">Attention Score</span>
          </div>
          <div className="flex items-end gap-1 relative z-10">
            <span className={`text-4xl font-bold font-mono tabular-nums ${atColor}`}>
              {atPercent !== null ? atPercent : '—'}
            </span>
            <span className="text-[12px] text-zinc-500 mb-1">%</span>
          </div>
          <div className="h-2 rounded-full bg-[#1E3A5F]/40 overflow-hidden relative z-10">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${atBarColor} transition-all duration-700`}
              style={{ width: atPercent !== null ? `${atPercent}%` : '0%' }}
            />
          </div>
        </div>
      </div>

      {/* Error / Status */}
      {fetchStatus === 'error' && errorMsg && (
        <p className="mt-3 text-[10px] text-red-400/80 font-mono truncate">
          ⚠ {errorMsg}
        </p>
      )}
      {!apiUrl && (
        <p className="mt-2 text-[10px] text-[#2D5A8E]">
          Set <code className="text-[#F97316]/70">NEXT_PUBLIC_EEG_API_URL</code> to connect
        </p>
      )}
    </div>
  );
}
